import React, { useState, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/Select';
import { Button } from '../components/ui/Button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/Table';
import {
  ChevronUp,
  ChevronDown,
  Copy,
  Search,
  Filter,
  Clock,
  AlertCircle,
  CheckCircle,
  Calendar,
  User,
  Trash2,
} from 'lucide-react';
import { Badge } from '../components/ui/Badge';

interface ReviewTableProps {
  reviews: any[];
  onDelete: (ids:number[])=>void;
}

const ReviewTable: React.FC<ReviewTableProps> = ({ reviews, onDelete }) => {
  const [searchTerm,setSearchTerm]=useState('');
  const [statusFilter,setStatusFilter]=useState('all');
  const [sortBy,setSortBy]=useState<'title'|'status'|'deadline'|'reviewer'|'createdAt'>('deadline');
  const [asc,setAsc]=useState(true);
  const PAGE_SIZE=10;
  const [page,setPage]=useState(1);
  const [selected,setSelected]=useState<Set<number>>(new Set());

  const toggleSort=(col:any)=>{
    if(sortBy===col) setAsc(!asc); else {setSortBy(col); setAsc(true);}  };

  const filtered=useMemo(()=>{
    return reviews.filter(r=>{
      const txt=searchTerm.toLowerCase();
      const matches=r.title.toLowerCase().includes(txt)|| (r.reviewer_email||'').toLowerCase().includes(txt);
      const statusOk=statusFilter==='all'|| r.status===statusFilter;
      return matches&&statusOk;
    });
  },[reviews,searchTerm,statusFilter]);

  const sorted=useMemo(()=>{
    return [...filtered].sort((a,b)=>{
      const mul=asc?1:-1;
      switch(sortBy){
        case 'title':
          return mul * a.title.localeCompare(b.title);
        case 'status':
          return mul * a.status.localeCompare(b.status);
        case 'deadline':
          return mul * (new Date(a.deadline || a.dueDate).getTime() - new Date(b.deadline || b.dueDate).getTime());
        case 'reviewer':
          return mul * ((a.reviewer_email || a.reviewer_row?.reviewer_email || '').localeCompare(b.reviewer_email || b.reviewer_row?.reviewer_email || ''));
        case 'createdAt':
          return mul * (new Date(a.created_at || a.createdAt).getTime() - new Date(b.created_at || b.createdAt).getTime());
        default:return 0;
      }
    });
  },[filtered,sortBy,asc]);

  const totalPages=Math.ceil(sorted.length/PAGE_SIZE)||1;
  const paginated=sorted.slice((page-1)*PAGE_SIZE,page*PAGE_SIZE);

  const toggleSelect=(id:number)=>{setSelected(prev=>{const s=new Set(prev); s.has(id)?s.delete(id):s.add(id); return s;});};
  const allIds=paginated.map(r=>r.id);
  const allSelected=allIds.length>0 && allIds.every(id=>selected.has(id));
  const someSelected=selected.size>0 && !allSelected;
  const toggleSelectAll=()=>{setSelected(prev=>{if(allSelected) return new Set(); const s=new Set(prev); allIds.forEach(id=>s.add(id)); return s;});};

  const handleDelete=()=>{if(selected.size===0) return; if(!window.confirm('Delete selected reviews?')) return; onDelete(Array.from(selected)); setSelected(new Set());};

  const formatDate=(d:any)=> new Date(d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });

  const statusConfig:any = {
    open: {
      label: 'Open',
      icon: Clock,
      color: 'bg-blue-100 text-blue-800 border-blue-200',
      iconColor: 'text-blue-600',
    },
    'changes-needed': {
      label: 'Changes Needed',
      icon: AlertCircle,
      color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      iconColor: 'text-yellow-600',
    },
    approved: {
      label: 'Approved',
      icon: CheckCircle,
      color: 'bg-green-100 text-green-800 border-green-200',
      iconColor: 'text-green-600',
    },
    overdue: {
      label: 'Overdue',
      icon: AlertCircle,
      color: 'bg-red-100 text-red-800 border-red-200',
      iconColor: 'text-red-600',
    },
  };

  const getStatusBadge = (status:string, deadline?: string | Date | null) => {
    const now = new Date();
    let deadlineDate: Date | null = null;
    if (deadline) {
      deadlineDate = typeof deadline === 'string' ? new Date(deadline) : deadline;
    }
    if (status === 'open' && deadlineDate && deadlineDate < now) {
      const cfg = statusConfig['overdue'];
      const Icon = cfg.icon;
      return (
        <Badge variant="outline" className={cfg.color}>
          <Icon className={`h-3 w-3 mr-1 ${cfg.iconColor}`} />
          {cfg.label}
        </Badge>
      );
    }
    const cfg = statusConfig[status] || { label: status };
    if (!cfg.icon) return cfg.label;
    const Icon = cfg.icon;
    return (
      <Badge variant="outline" className={cfg.color}>
        <Icon className={`h-3 w-3 mr-1 ${cfg.iconColor}`} />
        {cfg.label}
      </Badge>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Your Reviews</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col sm:flex-row gap-4 mb-4 items-start sm:items-center">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by title or reviewer..."
              value={searchTerm}
              onChange={(e)=>setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-48">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="changes-needed">Changes Needed</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
            </SelectContent>
          </Select>

          {/* Delete selected */}
          <Button
            variant="outline"
            disabled={selected.size===0}
            onClick={handleDelete}
            className="border-red-300 text-red-600 hover:bg-red-50 focus:ring-red-500"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Delete
            {selected.size>0 && ` (${selected.size})`}
          </Button>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-4"><input type="checkbox" onChange={toggleSelectAll} checked={allSelected} ref={el=>{if(el) el.indeterminate=someSelected}}/></TableHead>
              <TableHead className="cursor-pointer" onClick={()=>toggleSort('title')}>Title {sortBy==='title'&&(asc?<ChevronUp className="inline w-3 h-3"/>:<ChevronDown className="inline w-3 h-3"/>)}</TableHead>
              <TableHead className="cursor-pointer" onClick={()=>toggleSort('createdAt')}> 
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  Created On {sortBy==='createdAt'&&(asc?<ChevronUp className="inline w-3 h-3"/>:<ChevronDown className="inline w-3 h-3"/>)}
                </div>
              </TableHead>
              <TableHead className="cursor-pointer" onClick={()=>toggleSort('status')}>Status {sortBy==='status'&&(asc?<ChevronUp className="inline w-3 h-3"/>:<ChevronDown className="inline w-3 h-3"/>)}</TableHead>
              <TableHead className="cursor-pointer" onClick={()=>toggleSort('deadline')}>Due Date {sortBy==='deadline'&&(asc?<ChevronUp className="inline w-3 h-3"/>:<ChevronDown className="inline w-3 h-3"/>)}</TableHead>
              <TableHead className="cursor-pointer" onClick={()=>toggleSort('reviewer')}>Reviewer {sortBy==='reviewer'&&(asc?<ChevronUp className="inline w-3 h-3"/>:<ChevronDown className="inline w-3 h-3"/>)}</TableHead>
              <TableHead>
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  Last Reminder Sent
                </div>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginated.map(r=> (
              <TableRow key={r.id} className="hover:bg-muted/50">
                <TableCell className="w-4"><input type="checkbox" checked={selected.has(r.id)} onChange={()=>toggleSelect(r.id)}/></TableCell>
                <TableCell className="flex gap-2 items-center"><a href={`/review/${r.review_link}`} target="_blank" rel="noreferrer" className="underline">{r.title}</a><button onClick={()=>navigator.clipboard.writeText(`${window.location.origin}/review/${r.review_link}?id=${r.reviewer_row?.reviewer_link || ''}`)}><Copy className="w-4 h-4"/></button></TableCell>
                <TableCell>{formatDate(r.created_at || r.createdAt)}</TableCell>
                <TableCell>{getStatusBadge(r.status, r.deadline || r.dueDate)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    {(r.deadline || r.dueDate) ? formatDate(r.deadline||r.dueDate) : <span className="text-muted-foreground">Not set</span>}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    {r.reviewer_email || r.reviewer_row?.reviewer_email || '—'}
                  </div>
                </TableCell>
                <TableCell>
                  {r.lastReminderSent ? formatDate(r.lastReminderSent) : <span className="text-muted-foreground">—</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {totalPages>1 && (
          <div className="flex justify-center items-center gap-2 mt-4">
            <Button variant="outline" onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1}>Prev</Button>
            <span className="text-sm">Page {page} of {totalPages}</span>
            <Button variant="outline" onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages}>Next</Button>
          </div>
        )}
        {/* bottom delete button removed */}
      </CardContent>
    </Card>
  );
};

export default ReviewTable; 